/* eslint-disable camelcase */
import * as pc from 'playcanvas';
import gsap from 'gsap';
import {MaterialManager} from './MaterialManager';
import {IRWN} from './IRWN';
import {FSM} from './fsm/FSM';
import {FSM_PLANT, FSM_PLANT_BREATH} from './fsm/FSM_PLANT';
import {Component_DeformRipple} from './components/Component_DeformRipple';
import {IRWNCamera} from './IRWNCamera';
import {MouseManager} from './MouseManager';
import {AudioManager} from './AudioManager';
import {Component_BlendShape} from './components/Component_BlendShape';
import {BreathSystem} from './BreathSystem';
import {CreatureManager} from './CreatureManager';
import {AssetLoader} from './AssetLoader';
import {UserComponentManager} from './components/UserComponentManager';
import {LandingPageScene} from './LandingPageScene';
import {noise} from './../noise';
import {Component_Fly} from './components/Component_Fly';
import {Blob} from './Blob';
import {SceneSwitcher} from './SceneSwitcher';
import {timeToNextOccurenceOfHour} from '../utils/time';
import AxL from '../axl';

export const PlantScene = {
  // Plant morphs (blendshapes)
  bgDistance: -5,
  bgFluidScaleRate: 1,
  BlendShapeParams: {
    time: 0,
    rate_standard: 1,
    rate_breath: 20,
  },
  DeformParams: {
    rippleMin: 0.05,
    rippleMax: 0.5,
    ripplePower: 0.2,
  },
  LightParams: {
    pos: {x: 0, y: -2, z: 2.236},
    rot: {x: 0, y: 0, z: 180},
    col: {r: 70, g: 123, b: 198},
    innerConeAngle: 0,
    outerConeAngle: 70,
    range: 12,
    intensity: 0.01,
    // noise - intensity
    noise_intensity: {
      origin: 0.01,
      strength_normal: 0.01,
      strength_breathe: 0.02,
      counter: 0,
      rate: 0.2,
      range: 0.005,
    },
    noise_roamX: {origin: 0, counter: 33, rate: 0.01, range: 8},
    noise_rotation: {origin: 180, counter: 66, rate: 0.05, range: 20},
  },
  BreatheParams: {
    breatheBtnTime: 0,
    opacity: 1,
    rate: 0.1,
    amp: 0.04,
    btnCol: {},
    btnCol_over: {},
    btnCol_active: {},
    icon_breathe_scale: 1,
    icon_breathe_opacity: 1,
    icon_pause_scale: 0.5,
    icon_pause_opacity: 0,
    lightMultiplier: 1.5,
  },
  CreatureParams: {
    creatureLandCounter: 0,
    waspLandTimer: 0,
    waspLandTimerMin: 4,
    waspLandTimerMax: 25,
    creatureOffsetZ: 4,
  },

  children: [],

  init: function(playcanvasApp) {
    this.playcanvasApp = playcanvasApp;
    playcanvasApp.on('MOUSE_UPDATE', this.onMouseUpdate);
    this.children = [];
    this.loaded = false;

    this.vertLocator = new pc.Entity('vertLocator');
    this.playcanvasApp.root.addChild(this.vertLocator);
    this.switchRevealTimer = null;
  },
  addChild: function(entity, parent) {
    this.children.push(entity);
    parent.addChild(entity);
  },
  load: function() {
    this.loaded = false;
    this.pcApp = this.playcanvasApp;
    this.appJson = IRWN.getAppJson();
    this.plantJson = IRWN.getPlantJson();
    this.root = this.pcApp.root;

    // new plant vars
    this.plantID = IRWN.currentPlantID;
    this.plantConfig = IRWN.plantConfigs[this.plantID];
    LandingPageScene.unload();
    AssetLoader.loadPlantAssets(
        this.plantJson,
        this.plantID,
        this.onAssetLoadComplete,
    );
  },

  unload: function() {
    if (FSM.currentState == FSM_PLANT_BREATH) {
      BreathSystem.stop();
    }

    this.children.forEach((child) => {
      child.destroy();
      // console.log(child.name + " unloaded");
    });
    this.children = [];
    this.c_light = null;
    this.loaded = false;
  },

  onAssetLoadComplete: function() {},

  setup: function() {
    this.clearPollenOccupants();

    PlantScene.breatheModeActive = false;
    ////////////////////////////////////////////////////////////////
    // Plant is loaded - set the scene up
    ////////////////////////////////////////////////////////////////
    // create scene structure
    this.setupComponents();
    this.setupBackground();
    this.setupCameras();
    this.setupPlant();
    this.setupParticles();
    this.setupLights();
    this.setupCreatures();
    this.setupForeground();
    this.setupAudio();
    this.setupUI();

    BreathSystem.init(this.playcanvasApp);

    // VertSearch cube is used to locate pollen-emission vertices. Currently disabled as all flower locations are approved
    // this.e_vertSearchCube = new pc.Entity('e_vertSearchCube');
    // this.e_vertSearchCube.addComponent('render', {
    //   type: 'box',
    // });
    // this.e_vertSearchCube.setLocalScale(0.05, 0.05, 0.05);
    // PlantScene.addChild(this.e_vertSearchCube, this.root);
    // this.e_vertSearchCube.enabled = false;

    this.loaded = true;
    this.reveal();
  },

  setupComponents: function() {
    Component_DeformRipple.MasterSettings.plantScene = this;
    // reset components
    UserComponentManager.flush();
  },

  ////////////////////////////////////////////////////////////////
  //                                          Background Entities
  ////////////////////////////////////////////////////////////////
  createImagePlane: function(json, parent, name = '') {
    const itemName = json.textureName.slice(0, -4);
    const mat = MaterialManager.createMaterial(
        itemName,
        AssetLoader.textureAssets[json.textureName],
        json.lit,
        json.transparent,
    );

    // tint the amterial if required
    if (json.em_useTint) {
      mat.emissiveTint = true;
      const col = json.em_colour;
      mat.emissive = new pc.Color(col.r, col.g, col.b);
      // console.log(`${name} + '    requires tint...  ' + ${col.r} , ${col.g} , ${col.b}`);
      mat.emissiveIntensity = json.em_intensity / 2;
    } else {
      mat.emissiveIntensity = json.em_intensity;
    }
    mat.opacity = json.op_intensity;

    const e = new pc.Entity('e_' + itemName);
    e.addComponent('render', {
      type: 'plane',
      material: mat,
    });
    PlantScene.addChild(e, parent);

    // fg element setup
    e.name = name === '' ? 'e_' + itemName : name;
    e.setLocalEulerAngles(90, 0, json.rot);
    e.setLocalPosition(json.pos.x, json.pos.y, json.pos.z);
    e.setLocalScale(json.scl.x, json.scl.y, json.scl.z);
    return e;
  },
  createTurbulentImage: function(json, parent, name = '', timeOffset = 0) {
    const itemName = json.textureName.slice(0, -4);
    const mat = MaterialManager.createTurbulentImageMaterial(
        itemName,
        AssetLoader.textureAssets[json.textureName],
        timeOffset,
    );
    const e = new pc.Entity(name);
    e.addComponent('render', {
      type: 'plane',
      material: mat,
    });
    PlantScene.addChild(e, parent);
    e.setLocalEulerAngles(90, 0, json.rot);
    e.setLocalPosition(json.pos.x, json.pos.y, json.pos.z);
    e.setLocalScale(json.scl.x, json.scl.y, json.scl.z);
    return e;
  },
  setupBackground: function() {
    // Background Plane
    const bgJson = this.plantJson.imagePlanes.bg;
    this.e_bg = this.createImagePlane(bgJson, this.root);
    this.e_bg.render.material.emissiveMapTiling = new pc.Vec2(2, 2);
    this.e_bg.render.material.update();

    // Horizon plane
    const horizonJson = this.plantJson.imagePlanes.horizon;
    this.e_horizon = this.createImagePlane(horizonJson, this.root, false, true);
    this.e_horizon.name = 'e_horizon';
  },
  setupCameras: function() {
    this.cameraDistance_normal = 6;
    this.breatheCamPos = this.plantJson.breatheCamPos;
    this.breatheCamRot = this.plantJson.breatheCamRot;
    this.breatheCamFov = this.plantJson.breatheCamFov;
  },
  setupForeground: function() {
    this.envPlants = [];
    this.plantJson.imagePlanes.list.forEach((imgData) => {
      this.envPlants.push({
        pos: imgData.pos,
        entity: this.createImagePlane(
            imgData,
            PlantScene.root,
            imgData.entityName,
        ),
      });
    });
  },
  setupPlant: function() {
    this.e_plantContainer = new pc.Entity('e_plantContainer');
    this.e_plantContainer.setLocalPosition(
        this.plantJson.plantPos.x,
        this.plantJson.plantPos.y,
        this.plantJson.plantPos.z,
    );
    this.e_plantContainer.setLocalScale(
        this.plantJson.plantScale,
        this.plantJson.plantScale,
        this.plantJson.plantScale,
    );
    PlantScene.addChild(this.e_plantContainer, this.root);

    //------------------------------------> BUILD PLANT FROM JSON
    this.plantJson.plantStems.forEach((partJson) => {
      // add entity
      const e_plantPart = AssetLoader.modelAssets[
          partJson.name
      ].resource.instantiateRenderEntity({});
      e_plantPart.setLocalPosition(
          partJson.pos.x,
          partJson.pos.y,
          partJson.pos.z,
      );
      e_plantPart.setLocalEulerAngles(
          partJson.rot.x,
          partJson.rot.y,
          partJson.rot.z,
      );
      e_plantPart.setLocalScale(partJson.scl.x, partJson.scl.y, partJson.scl.z);
      e_plantPart.name = 'e_plantPart' + partJson.index;
      const renderer = e_plantPart.findComponent('render');
      PlantScene.addChild(e_plantPart, this.e_plantContainer);

      for (
        let materialIndex = 0;
        materialIndex < partJson.materialSlots.length;
        materialIndex++
      ) {
        const materialSlot = partJson.materialSlots[materialIndex];
        const mat = MaterialManager.createMaterial(
            materialSlot.textureName,
            AssetLoader.textureAssets[materialSlot.textureName],
        );
        renderer.meshInstances[materialIndex].material = mat;
      }
      // Add Compoments
      this.addDeformerComponent(e_plantPart, partJson);
      this.addBlendShapeComponent(e_plantPart, partJson);
    });
  },
  //------------------------------------> COMPONENTS
  // Setup the RIPPLE_DEFORM component on a plant stem (config from plant json)
  addDeformerComponent: function(entity, partJson) {
    const config = partJson.deform;
    const c = Component_DeformRipple.attachToEntity(entity);
    c.offset = this.plantJson.deformerOffset * partJson.index;
    c.scale = config.scale;
    c.rate = config.scale;
    c.normX = config.normX;
    c.index = partJson.index;
  },

  // Setup the RIPPLE_DEFORM component on a plant stem (config from plant json)
  addBlendShapeComponent: function(entity, partJson) {
    const c = Component_BlendShape.attachToEntity(entity);
    const e_glb = entity.children[0];

    for (let i = 0; i < partJson.materialSlots.length; i++) {
      const slot = partJson.materialSlots[i];
      if (slot.blendRate) {
        c.blends.push({
          morphInstance: e_glb.render.meshInstances[i].morphInstance,
          mesh: e_glb.render.meshInstances[i].mesh,
          offset: pc.math.random(0, 3),
          rate: slot.blendRate,
        });
      }
    }
  },

  setupParticles: function() {
    function mk_particles(name, texture, parent, posX, rot, cfg) {
      const e = new pc.Entity(name);
      e.addComponent('particlesystem', {
        blendType: pc.BLEND_ADDITIVE,
        colorMap: texture,
        loop: true,
        preWarm: true,
        autoPlay: cfg.autoPlay,
        numParticles: cfg.numParticles,
        lifetime: cfg.lifetime,
        rate: cfg.emissionRate1,
        rate2: cfg.emissionRate2,
        startAngle: cfg.startAngle1,
        startAngle: cfg.startAngle2,
        localVelocityGraph: new pc.CurveSet(cfg.curves.localVelocity1),
        localVelocityGraph2: new pc.CurveSet(cfg.curves.localVelocity2),
        velocityGraph: new pc.CurveSet(cfg.curves.worldVelocity1),
        velocityGraph2: new pc.CurveSet(cfg.curves.worldVelocity2),
        scaleGraph: new pc.Curve(cfg.curves.scale1),
        scaleGraph2: new pc.Curve(cfg.curves.scale2),
        emitterExtents: new pc.Vec3(
            cfg.emitterExtents.x,
            cfg.emitterExtents.y,
            cfg.emitterExtents.z,
        ),
        alphaGraph: new pc.Curve(cfg.curves.alpha1),
        alphaGraph2: new pc.Curve(cfg.curves.alpha2),
      });
      e.setLocalPosition(posX, cfg.posY, cfg.posZ);
      e.setLocalEulerAngles(0, rot, 0);
      PlantScene.addChild(e, parent);
      return e;
    }
    // create FG particles
    const particleJson = this.appJson.particles;

    // foreground particles
    const fgTexture = AssetLoader.textureAssets['particleFG.basis'].resource;
    this.e_particles_fg_left = mk_particles(
        'e_particles_fg_left',
        fgTexture,
        this.root,
        -particleJson.fg.xSpacing,
        0,
        particleJson.fg,
    );
    this.e_particles_fg_right = mk_particles(
        'e_particles_fg_right',
        fgTexture,
        this.root,
        particleJson.fg.xSpacing,
        180,
        particleJson.fg,
    );

    // Background particles
    const bgTexture = AssetLoader.textureAssets['particleBG.basis'].resource;
    this.e_particles_bg_left = mk_particles(
        'e_particles_bg_left',
        bgTexture,
        this.root,
        -particleJson.bg.xSpacing,
        0,
        particleJson.bg,
    );
    this.e_particles_bg_right = mk_particles(
        'e_particles_bg_right',
        bgTexture,
        this.root,
        particleJson.bg.xSpacing,
        180,
        particleJson.bg,
    );

    // pollen emission
    const pollenTexture =
      AssetLoader.textureAssets['particlePollen.basis'].resource;
    this.e_particles_shake = mk_particles(
        'e_particles_shake',
        pollenTexture,
        this.root,
        0,
        0,
        particleJson.shake,
    );
    this.particles_shake = this.e_particles_shake.particlesystem;

    // if _6_HYPO - load the Dandelion particles
    if (this.plantID == '_6_HYPO') {
      const _6_HYPO_texture =
        AssetLoader.textureAssets['_6_HYPO_particle.basis'].resource;
      this.e_particles_6_HYPO = mk_particles(
          'e_particles_6_HYPO',
          _6_HYPO_texture,
          this.root,
          0,
          0,
          particleJson.shake_6_HYPO,
      );
      this.particles_6_HYPO = this.e_particles_6_HYPO.particlesystem;
    }
  },

  disturbPlantStem: function(stemIndex) {
    const pollenVertsOnStem = [];
    this.PollenVerts[this.plantID].forEach((p) => {
      if (p.stemIndex == stemIndex) {
        pollenVertsOnStem.push(p);
      }
    });

    if (pollenVertsOnStem.length > 0) {
      // get a random pollen vert obj
      const randomPick = Math.floor(Math.random() * pollenVertsOnStem.length);
      const vertIndex = pollenVertsOnStem[randomPick].vertIndex;
      this.doPollenParticles(stemIndex, vertIndex);
    }
    Component_Fly.disturbCreaturesOnStem(stemIndex);
  },
  doPollenParticles: function(stemIndex, vertIndex) {
    const vertPos = this.vert(stemIndex, vertIndex);
    // if _HYPO and Dandelion vert - do the special pollen particles
    if (vertIndex == 14288) {
      if (this.plantID == '_6_HYPO') {
        this.e_particles_6_HYPO.setPosition(vertPos.x, vertPos.y, vertPos.z);
        this.particles_6_HYPO.play();
      }
    } else {
      this.e_particles_shake.setPosition(vertPos.x, vertPos.y, vertPos.z);
      this.particles_shake.play();
    }
  },
  stopShakeParticles: function() {
    try {
      this.particles_shake.stop();
    } catch (error) {
      // console.log(error);
    }
    if (this.particles_6_HYPO != null) {
      this.particles_6_HYPO.stop();
    }
  },
  setupLights: function() {
    PlantScene.LightParams.noiseCounter = 0;

    const lightJson = this.plantJson.light;
    this.e_light = new pc.Entity('e_light');
    PlantScene.addChild(this.e_light, this.root);

    // add light component with json settings
    this.c_light = this.e_light.addComponent('light', {
      type: 'spot',
      color: new pc.Color(
          lightJson.col.r * 255,
          lightJson.col.g * 255,
          lightJson.col.b * 255,
      ),
      innerConeAngle: 0,
      outerConeAngle: 70,
      intensity: 0.1,
      range: 16,
      castShadows: false,
      affectDynamic: true,
      affectLightMapped: false,
    });

    this.e_light.setLocalPosition(
        lightJson.pos.x,
        lightJson.pos.y,
        lightJson.pos.z,
    );
    this.e_light.setLocalEulerAngles(0, 0, lightJson.rot);
  },

  //------------------------------------> SETUP CREATURES
  setupCreatures: function() {
    CreatureManager.showNewScene();
  },

  setupAudio: function() {
    AudioManager.playPlantScene(this.plantID);
  },

  ////////////////////////////////////////////////////////////////
  //                                                           UI
  ////////////////////////////////////////////////////////////////
  setupUI() {
    const breatheBtnColours = IRWN.palettes[this.plantConfig.palette];
    const breatheParams = PlantScene.BreatheParams;
    breatheParams.btnCol = breatheBtnColours.neutral;
    breatheParams.btnCol_over = breatheBtnColours.hover;
    breatheParams.btnCol_active = breatheBtnColours.active;

    // event listeners
    document
        .getElementById('breatheBtnBlob')
        .addEventListener('click', PlantScene.onBreatheToggle);
    document
        .getElementById('breatheBtnBlob')
        .addEventListener('mouseenter', PlantScene.onBreathButtonRollover);
    document
        .getElementById('breatheBtnBlob')
        .addEventListener('mouseleave', PlantScene.onBreathButtonRollout);
    document.getElementById('breatheBtnBlob').style.fill = `rgb( ${
      PlantScene.BreatheParams.btnCol.r * 255
    }, 
          ${PlantScene.BreatheParams.btnCol.g * 255}, 
          ${PlantScene.BreatheParams.btnCol.b * 255})`;

    gsap.to('#breatheBtn_icon_on', {opacity: 0, scale: 0.25, duration: 0});
    gsap.to('#breatheBtn_icon_off', {opacity: 1, scale: 1, duration: 0});

    // update CSS button colours
    const palette = IRWN.palettes[this.plantConfig.palette];
    document
        .getElementById('btn_container')
        .style.setProperty(
            '--btn-bgCol_over',
            `rgba(${palette.hover.r * 255}, ${palette.hover.g * 255}, ${
              palette.hover.b * 255
            }, 0.5)`,
        );
    document
        .getElementById('btn_container')
        .style.setProperty(
            '--btn-bgCol_click',
            `rgba(${palette.active.r * 255}, ${palette.active.g * 255}, ${
              palette.active.b * 255
            }, 0.5)`,
        );

    // set light / dark theme depending on scene
    const useDarkTopUI = this.plantConfig.useDarkUI;
    document.body.style.setProperty(
        '--top_ui_colour',
      useDarkTopUI ? 'black' : 'white',
    );

    PlantScene.startSwitchRevealTimer(6);
  },
  revealCursorGlow: function() {
    if (!pc.platform.touch) {
      gsap.killTweensOf('.cursor_glow', 'opacity');
      gsap.to('.cursor_glow', {opacity: 1, duration: 2});
    }
  },
  hideCursorGlow: function() {
    if (!pc.platform.touch) {
      gsap.killTweensOf('.cursor_glow', 'opacity');
      gsap.to('.cursor_glow', {opacity: 0, duration: 1});
    }
  },
  startSwitchRevealTimer: function(duration) {
    PlantScene.switchRevealTimer = gsap.delayedCall(duration, () => {
      SceneSwitcher.show(this.plantConfig.index);
    });
  },
  killSwitchRevealTimer: function() {
    if (PlantScene.switchRevealTimer != null) {
      PlantScene.switchRevealTimer.kill();
    }
  },

  breatheBtnCount: 1,

  updateBreatheBtnPath: function(dt) {
    this.breatheBtnCount += dt * 0.5;
    const pathData = Blob.getPath(110, this.breatheBtnCount);
    document.querySelector('#breatheBtnBlob').setAttribute('d', pathData);
  },

  onBreathButtonRollover: function() {
    document.querySelector('#breatheBtnBlob').style.fill = `rgb(
      ${PlantScene.BreatheParams.btnCol_over.r * 255}, 
      ${PlantScene.BreatheParams.btnCol_over.g * 255}, 
      ${PlantScene.BreatheParams.btnCol_over.b * 255} 
      )`;
  },
  onBreathButtonRollout: function() {
    if (PlantScene.breatheModeActive) {
      document.getElementById('breatheBtnBlob').style.fill = `rgb( ${
        PlantScene.BreatheParams.btnCol_active.r * 255
      }, 
          ${PlantScene.BreatheParams.btnCol_active.g * 255}, 
          ${PlantScene.BreatheParams.btnCol_active.b * 255})`;
    } else {
      document.getElementById('breatheBtnBlob').style.fill = `rgb( ${
        PlantScene.BreatheParams.btnCol.r * 255
      }, 
          ${PlantScene.BreatheParams.btnCol.g * 255}, 
          ${PlantScene.BreatheParams.btnCol.b * 255})`;
    }
  },
  onBreatheToggle: function() {
    PlantScene.breatheModeActive = !PlantScene.breatheModeActive;

    if (PlantScene.breatheModeActive) {
      // analytics
      IRWN.analyticsReport('click_breathe_mode_start', 'breathe_mode_start');

      FSM.setState(FSM_PLANT_BREATH);
      document.getElementById('breatheBtnBlob').style.fill = `rgb( ${
        PlantScene.BreatheParams.btnCol_active.r * 255
      }, 
        ${PlantScene.BreatheParams.btnCol_active.g * 255}, 
        ${PlantScene.BreatheParams.btnCol_active.b * 255})`;
      gsap.to('#breatheBtn_icon_off', {
        opacity: 0,
        scale: 0.25,
        duration: 0.25,
      });
      gsap.to('#breatheBtn_icon_on', {
        opacity: 1,
        scale: 1,
        duration: 0.5,
        ease: 'Back.out(2)',
      });
    } else {
      FSM.setState(FSM_PLANT);

      // analytics
      IRWN.analyticsReport('click_breathe_mode_stop', 'breathe_mode_stop');
      document.getElementById('breatheBtnBlob').style.fill = `rgb( ${
        PlantScene.BreatheParams.btnCol.r * 255
      }, 
        ${PlantScene.BreatheParams.btnCol.g * 255}, 
        ${PlantScene.BreatheParams.btnCol.b * 255})`;

      gsap.to('#breatheBtn_icon_on', {
        opacity: 0,
        scale: 0.25,
        duration: 0.25,
      });
      gsap.to('#breatheBtn_icon_off', {
        opacity: 1,
        scale: 1,
        duration: 0.5,
        ease: 'Back.out(2)',
      });
    }
  },
  onBreatheToggleTweenUpdate: function() {
    const scl_breath = PlantScene.BreatheParams.icon_breathe_scale;
    const opacity_breath = PlantScene.BreatheParams.icon_breathe_opacity;
    const scl_pause = PlantScene.BreatheParams.icon_pause_scale;
    const opacity_pause = PlantScene.BreatheParams.icon_pause_opacity;

    // scales
    PlantScene.img_breatheIcon.setLocalScale(
        scl_breath,
        scl_breath,
        scl_breath,
    );
    PlantScene.img_pauseIcon.setLocalScale(scl_pause, scl_pause, scl_pause);

    // opacity
    PlantScene.img_breatheIcon.element.opacity = opacity_breath;
    PlantScene.img_pauseIcon.element.opacity = opacity_pause;
  },
  hideUI: function() {
    if (this.grp_breatheBtn) {
      this.grp_breatheBtn.enabled = false;
    }
  },

  showWelcomesMessages: function() {
    const initialDelay = 4;

    let msg1 = `
Sit back and relax with
${this.plantConfig.fullName}`;

    document.getElementById('plant_welcome_1').textContent = msg1.trim();

    // fade in 1
    // welcome message and time
    gsap.killTweensOf('.plant_welcome_1', 'opacity');
    gsap.to('.plant_welcome_1', {
      opacity: 1,
      delay: initialDelay,
      duration: 2,
      ease: 'strong.in',
    });
    // fade out 1 after 5 seconds
    gsap.to('.plant_welcome_1', {
      opacity: 0,
      delay: initialDelay + 5,
      duration: 2,
      ease: 'strong.in',
    });

    // fade in 2
    const welcomeMessage2 =
      IRWN.totalCurrentUsers >= IRWN.welcomeMessage_minumumUserCount ?
        `You are 1 / ${IRWN.totalCurrentUsers} visitors` :
        '';
    document.getElementById('plant_welcome_2').textContent = welcomeMessage2;
    gsap.killTweensOf('.plant_welcome_2', 'opacity');
    gsap.to('.plant_welcome_2', {
      opacity: 1,
      delay: initialDelay + 7,
      duration: 2,
      ease: 'strong.in',
    });
    // fade out 2 after 5 seconds
    gsap.to('.plant_welcome_2', {
      opacity: 0,
      delay: initialDelay + 13,
      duration: 2,
      ease: 'strong.in',
    });
  },
  makeTimeString(date=null) {
    return 'Opening now';
    date = date || new Date();
    let hrs = date.getHours();
    const suffix = hrs >= 12 ? 'pm' : 'am';
    hrs = hrs == 12 ? 12 : hrs % 12;
    const mins = date.getMinutes().toLocaleString('en-US', {
      minimumIntegerDigits: 2,
      useGrouping: false,
    });
    return hrs + ':' + mins + ' ' + suffix;
  },
  makeTimeDeltaString(targetPlant, date=null) {
    const sceneIdx = IRWN.getIndexFromPlant(targetPlant);
    const plantStartHour = IRWN.getTimeForSceneStart(sceneIdx);

    const [deltaHrs, DeltaMin] = timeToNextOccurenceOfHour(plantStartHour);
    if (deltaHrs > 0) return `Opens in ${deltaHrs}h ${DeltaMin}min`;
    else return `Opens in ${DeltaMin}min`;
  },
  hideWelcomeMessages: function() {
    // kill tweens to welcome messages
    gsap.killTweensOf('.plant_welcome_1', 'opacity');
    gsap.killTweensOf('.plant_welcome_2', 'opacity');
    // fade both out
    gsap.to('.plant_welcome_1', {opacity: 0, duration: 1, ease: 'strong.in'});
    gsap.to('.plant_welcome_2', {opacity: 0, duration: 1, ease: 'strong.in'});
  },
  ////////////////////////////////////////////////////////////////
  //                                                        UPDATE
  ////////////////////////////////////////////////////////////////
  update: function(dt) {
    // update camera via mouse
    IRWNCamera.setTarget(MouseManager.nx, MouseManager.ny);
    Component_DeformRipple.systemUpdate(dt);

    // if (IRWN_Debug.Params.vertFinderActive) {
    //   const vertPos = PlantScene.vert(IRWN_Debug.Params.targetStem, IRWN_Debug.Params.targetVert);
    //   this.e_vertSearchCube.setLocalScale(IRWN_Debug.Params.locatorSize, IRWN_Debug.Params.locatorSize, IRWN_Debug.Params.locatorSize);
    //   this.e_vertSearchCube.setPosition(vertPos.x, vertPos.y, vertPos.z);
    // }

    MaterialManager.setUniform(
        'breatheBtn',
        'uTime',
        PlantScene.BreatheParams.breatheBtnTime,
    );
    PlantScene.BreatheParams.breatheBtnTime += dt;

    // Light behaviour
    const lp = PlantScene.LightParams;
    if (this.c_light != null) {
      this.c_light.intensity = this.tickNoise(lp.noise_intensity, dt);
    }
    if (this.e_light != null) {
      this.e_light.setLocalPosition(
          this.tickNoise(lp.noise_roamX, dt),
          lp.pos.y,
          lp.pos.z,
      );
      this.e_light.setLocalEulerAngles(
          0,
          0,
          this.tickNoise(lp.noise_rotation, dt),
      );
    }

    this.updateBreatheBtnPath(dt);
  },
  tickNoise: function(noiseObj, dt) {
    noiseObj.counter += dt * noiseObj.rate;
    const n = noise.simplex2(noiseObj.counter, 0) * noiseObj.range;
    return noiseObj.origin + n;
  },
  reveal: function() {
    gsap.killTweensOf('#breathe_btn', 'opacity');
    gsap.to('#breathe_btn', {
      opacity: AxL.initialData.hidden_breathe_btn ? 0 : 1,
      delay: 1,
      duration: 2,
      ease: 'strong.in',
    });
    this.revealCursorGlow();
    document.getElementById('breathe_btn').style.cursor = 'pointer';
    document.getElementById('breathe_btn').style.pointerEvents = 'all';

    this.showWelcomesMessages();
  },
  hideBreatheBth() {
    gsap.killTweensOf('#breathe_btn', 'opacity');
    gsap.to('#breathe_btn', {opacity: 0, duration: 2, ease: 'strong.in'});
    document.getElementById('breathe_btn').style.cursor = 'none';
    document.getElementById('breathe_btn').style.pointerEvents = 'none';
  },
  hide: function() {
    PlantScene.hideCursorGlow();
    PlantScene.hideWelcomeMessages();
    this.hideBreatheBth();
  },
  onResize: function(w, h) {},
  resizeHandler: function(w, h, pxRatio) {
    if (this.loaded) {
      this.envPlants.forEach((img) => {
        const pos = img.pos;
        const e = img.entity;
        let fluidPos = IRWN.getfluidX(pos.x, -pos.z);
        if (fluidPos<0) {
          fluidPos = fluidPos = Math.min(fluidPos, -2);
        } else {
          fluidPos = fluidPos = Math.max(fluidPos, 2);
        }
        e.setLocalPosition(fluidPos, pos.y, pos.z);
      });
      // resize the BG
      const bgFluidScale =
        IRWN.aspectSqueeze * (25 * PlantScene.bgFluidScaleRate);
      this.e_bg.setLocalScale(bgFluidScale, bgFluidScale, bgFluidScale);
    }
  },
};

PlantScene.vert = function(stemIndex, vertIndex) {
  const masterStem = this.e_plantContainer.children[stemIndex];
  const stem = masterStem.children[0];
  const mesh = stem.render.meshInstances[0].mesh;

  const vertPositions = [];
  mesh.getPositions(vertPositions);
  const vi = vertIndex * 3;
  const posX = vertPositions[vi];
  const posY = vertPositions[vi + 1];
  const posZ = vertPositions[vi + 2];
  const vPos = new pc.Vec3(posX, posY, posZ);

  const t_vert = new pc.Mat4();
  t_vert.setTRS(vPos, new pc.Vec3(0, 0, 0), new pc.Vec3(1, 1, 1));
  const t_masterStem = masterStem.getLocalTransform();
  const t_stemSum = new pc.Mat4();
  t_stemSum.mulAffine2(t_masterStem, t_vert);
  const t_container = this.e_plantContainer.getLocalTransform();
  const target = new pc.Mat4();
  target.mulAffine2(t_container, t_stemSum);
  return target.getTranslation();
};

PlantScene.clearPollenOccupants = function() {
  const stemList = this.PollenVerts[this.plantID];
  stemList.forEach((vertObj) => {
    vertObj.occupant = null;
  });
};

PlantScene.getUnoccupiedPollenVerts = function() {
  const result = [];
  const stemList = this.PollenVerts[this.plantID];
  stemList.forEach((vertObj) => {
    if (vertObj.occupant == null && vertObj.canLand) {
      result.push(vertObj);
    }
  });
  return result;
};
PlantScene.PollenVerts = {
  _1_ANTHER: [
    {
      stemIndex: 11,
      vertIndex: 9817,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: false,
      particleType: 0,
    },
    {
      stemIndex: 11,
      vertIndex: 6350,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: false,
      particleType: 0,
    },
    {
      stemIndex: 11,
      vertIndex: 13957,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: false,
      particleType: 0,
    },
    {
      stemIndex: 11,
      vertIndex: 384,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },

    {
      stemIndex: 12,
      vertIndex: 9817,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },

    {
      stemIndex: 13,
      vertIndex: 9817,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 13,
      vertIndex: 15044,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 13,
      vertIndex: 12536,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
  ],

  _2_CONVO: [
    {
      stemIndex: 0,
      vertIndex: 3636,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 0,
      vertIndex: 5807,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
  ],
  _3_TRICHO: [
    {
      stemIndex: 1,
      vertIndex: 6350,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 2,
      vertIndex: 4059,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 3,
      vertIndex: 14396,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
  ],
  _4_ANA: [
    {
      stemIndex: 0,
      vertIndex: 10697,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 0,
      vertIndex: 4177,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },

    {
      stemIndex: 1,
      vertIndex: 15587,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 1,
      vertIndex: 9067,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 1,
      vertIndex: 3090,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },

    {
      stemIndex: 2,
      vertIndex: 10697,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 2,
      vertIndex: 6446,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },

    {
      stemIndex: 3,
      vertIndex: 2004,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 3,
      vertIndex: 10697,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
  ],

  _5_PETROR: [
    {
      stemIndex: 0,
      vertIndex: 1295,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },

    {
      stemIndex: 1,
      vertIndex: 6185,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 1,
      vertIndex: 1086,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 1,
      vertIndex: 15965,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },

    {
      stemIndex: 2,
      vertIndex: 1319,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 2,
      vertIndex: 6185,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 2,
      vertIndex: 11812,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 3,
      vertIndex: 2925,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 3,
      vertIndex: 8358,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },

    {
      stemIndex: 4,
      vertIndex: 11618,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 4,
      vertIndex: 6370,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 4,
      vertIndex: 1521,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },

    {
      stemIndex: 5,
      vertIndex: 1320,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
  ],
  _6_HYPO: [
    {
      stemIndex: 14,
      vertIndex: 14288,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: false,
      particleType: 1,
    },
    {
      stemIndex: 16,
      vertIndex: 8895,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: false,
      particleType: 0,
    },
  ],
  _7_MALVA: [
    {
      stemIndex: 1,
      vertIndex: 3634,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 1,
      vertIndex: 14500,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },

    {
      stemIndex: 3,
      vertIndex: 4177,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },

    {
      stemIndex: 5,
      vertIndex: 6894,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
  ],
  _8_NYMPH: [
    {
      stemIndex: 1,
      vertIndex: 9439,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 2,
      vertIndex: 4061,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 2,
      vertIndex: 747,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
  ],
  _9_MESEM: [
    {
      stemIndex: 0,
      vertIndex: 2381,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },

    {
      stemIndex: 1,
      vertIndex: 9628,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 1,
      vertIndex: 13726,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 1,
      vertIndex: 24309,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },

    {
      stemIndex: 2,
      vertIndex: 24309,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 2,
      vertIndex: 24309,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 2,
      vertIndex: 25201,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 2,
      vertIndex: 35525,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },

    {
      stemIndex: 3,
      vertIndex: 25201,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
  ],
  _10_CALEN: [
    {
      stemIndex: 5,
      vertIndex: 757,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 5,
      vertIndex: 12162,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },

    {
      stemIndex: 6,
      vertIndex: 33,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 6,
      vertIndex: 35580,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
  ],
  _11_HIERA: [
    {
      stemIndex: 0,
      vertIndex: 4535,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 0,
      vertIndex: 8455,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 0,
      vertIndex: 15991,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 0,
      vertIndex: 24711,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },

    {
      stemIndex: 1,
      vertIndex: 14434,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 1,
      vertIndex: 18139,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 1,
      vertIndex: 28064,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 1,
      vertIndex: 36105,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
  ],
  _12_HEMERO: [
    {
      stemIndex: 0,
      vertIndex: 2316,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 0,
      vertIndex: 3024,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 0,
      vertIndex: 1719,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
  ],
  _13_PAPA: [
    {
      stemIndex: 1,
      vertIndex: 12,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 3,
      vertIndex: 4011,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 4,
      vertIndex: 26551,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 6,
      vertIndex: 3684,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
    {
      stemIndex: 7,
      vertIndex: 8901,
      min: 0,
      max: 1,
      breathMode: true,
      occupant: null,
      canLand: true,
      particleType: 0,
    },
  ],
};
